// src/pages/Signup.jsx
import React, { useState } from 'react';
import {
  Box, VStack, FormControl, FormLabel, Input, Select,
  Button, Heading, Text
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { signup, checkUsername, login, conversationStarted } from '../api/auth';

export default function Signup() {
  const navigate = useNavigate();

  // Form state
  const [driverName, setDriverName] = useState('');
  const [truckNumber, setTruckNumber] = useState('');
  const [trailerNumber, setTrailerNumber] = useState('');
  const [mc, setMc] = useState('');
  const [dot, setDot] = useState('');
  const [email, setEmail] = useState('');
  const [contact, setContact] = useState('');
  const [company, setCompany] = useState('');
  const [equipment, setEquipment] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  // Error & loading state
  const [usernameError, setUsernameError] = useState('');
  const [signupError, setSignupError] = useState('');
  const [loading, setLoading] = useState(false);

  // Submit signup form
  const handleSignup = async e => {
    e.preventDefault();
    if (usernameError) return;
    setSignupError('');
    setLoading(true);
    try {
      await signup({
        username,
        password: password,
        driver_name: driverName,
        truck_number: truckNumber,
        trailer_number: trailerNumber,
        mc_number: mc,
        dot_number: dot,
        email,
        contact,
        company,
        equipment
      });
      // auto-login & init
      const { driver_id, session_id } = await login(username, password);
      await conversationStarted(driver_id);
      navigate('/search', { replace: true });
    } catch (err) {
      setSignupError(
        err.response?.status === 400
          ? err.response.data.detail || 'Signup failed'
          : 'An unexpected error occurred'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box maxW="md" mx="auto" mt={16} p={6} borderWidth={1} borderRadius="md">
      <VStack as="form" spacing={4} onSubmit={handleSignup}>
        <Heading size="lg">Create Account</Heading>

        <FormControl id="driverName" isRequired>
          <FormLabel>Driver Name</FormLabel>
          <Input
            placeholder="Full Name"
            value={driverName}
            onChange={e => setDriverName(e.target.value)}
          />
        </FormControl>

        <FormControl id="truckNumber" isRequired>
          <FormLabel>Truck Number</FormLabel>
          <Input
            placeholder="Truck Number"
            value={truckNumber}
            onChange={e => setTruckNumber(e.target.value)}
          />
        </FormControl>

        <FormControl id="trailerNumber" isRequired>
          <FormLabel>Trailer Number</FormLabel>
          <Input
            placeholder="Trailer Number"
            value={trailerNumber}
            onChange={e => setTrailerNumber(e.target.value)}
          />
        </FormControl>

        <FormControl id="mc" isRequired>
          <FormLabel>MC Number</FormLabel>
          <Input
            placeholder="MC Number"
            value={mc}
            onChange={e => setMc(e.target.value)}
          />
        </FormControl>

        <FormControl id="dot" isRequired>
          <FormLabel>DOT Number</FormLabel>
          <Input
            placeholder="DOT Number"
            value={dot}
            onChange={e => setDot(e.target.value)}
          />
        </FormControl>

        <FormControl id="email" isRequired>
          <FormLabel>Email</FormLabel>
          <Input
            type="email"
            placeholder="name@example.com"
            value={email}
            onChange={e => setEmail(e.target.value)}
          />
        </FormControl>

        <FormControl id="contact" isRequired>
          <FormLabel>Contact Number</FormLabel>
          <Input
            type="tel"
            placeholder="123-456-7890"
            value={contact}
            onChange={e => setContact(e.target.value)}
          />
        </FormControl>

        <FormControl id="company">
          <FormLabel>Company (if any)</FormLabel>
          <Input
            placeholder="Company name"
            value={company}
            onChange={e => setCompany(e.target.value)}
          />
        </FormControl>

        <FormControl id="equipment" isRequired>
          <FormLabel>Equipment Type</FormLabel>
          <Select
            placeholder="Select equipment"
            value={equipment}
            onChange={e => setEquipment(e.target.value)}
          >
            <option value="flatbed">Flatbed</option>
            <option value="dryvan">Dry Van</option>
            <option value="reefer">Reefer</option>
          </Select>
        </FormControl>

    
        <FormControl id="username" isRequired isInvalid={!!usernameError}>
          <FormLabel>Username</FormLabel>
          <Input
            value={username}
            onChange={e => setUsername(e.target.value)}
            onBlur={async () => {
              if (!username) return;
              try {
                const { data } = await checkUsername(username);
                if (!data.available) setUsernameError('Taken');
                else setUsernameError('');
              } catch {
                setUsernameError('Could not validate');
              }
            }}
          />
          {usernameError && <Text color="red.500">{usernameError}</Text>}
        </FormControl>

        <FormControl id="password" isRequired>
          <FormLabel>Password</FormLabel>
          <Input
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
          />
        </FormControl>

        {signupError && <Text color="red.500">{signupError}</Text>}

        <Button
          type="submit"
          colorScheme="brand"
          width="full"
          isLoading={loading}
          isDisabled={!!usernameError}
        >
          Sign Up
        </Button>
      </VStack>
    </Box>
  );
}